import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import PersonIcon from '@mui/icons-material/Person';
import TodayIcon from '@mui/icons-material/Today';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import { useNavigate, useSearchParams } from 'react-router-dom';
import calendarService from '../services/calendarService';
import projectService from '../services/projectService';
import taskService from '../services/taskService';
import userService from '../services/userService';

const LOCAL_EVENTS_KEY = 'calendar_local_events_v1';

const EVENT_TYPES = {
  meeting: { label: 'Meeting', icon: PersonIcon, color: '#1976d2' },
  deadline: { label: 'Deadline', icon: AccessTimeIcon, color: '#d32f2f' },
  task: { label: 'Task', icon: AssignmentIcon, color: '#ed6c02' },
  project: { label: 'Project', icon: BusinessCenterIcon, color: '#2e7d32' },
};

const EVENT_SOURCE_LABELS = {
  manual: 'Manual',
  task: 'Tasks',
  project: 'Projects',
};

const INITIAL_GOOGLE_STATUS = {
  configured: false,
  connected: false,
  google_email: '',
  calendar_id: 'primary',
  expiry_date: null,
};

const normalizeStatusKey = (statusRaw) => {
  const normalized = String(statusRaw || '').trim().toLowerCase();
  if (!normalized) return 'planned';
  if (normalized.includes('done') || normalized.includes('complete') || normalized.includes('closed')) return 'completed';
  if (normalized.includes('cancel') || normalized.includes('archived')) return 'cancelled';
  if (normalized.includes('progress') || normalized.includes('active') || normalized.includes('ongoing') || normalized.includes('doing')) return 'in_progress';
  return 'planned';
};

const toDateKey = (value) => {
  const text = String(value || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) return text.slice(0, 10);
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const loadLocalEvents = () => {
  try {
    const raw = localStorage.getItem(LOCAL_EVENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const saveLocalEvents = (events) => {
  try {
    localStorage.setItem(LOCAL_EVENTS_KEY, JSON.stringify(events));
  } catch (error) {}
};

const normalizeManualEvents = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      const dateKey = toDateKey(row?.date);
      if (!dateKey) return null;
      const type = EVENT_TYPES[row?.type] ? row.type : 'meeting';
      const fallbackColor = EVENT_TYPES[type]?.color || '#1976d2';
      return {
        id: row.id ?? `manual-${Date.now()}-${Math.random()}`,
        source: 'manual',
        sourceLabel: EVENT_SOURCE_LABELS.manual,
        title: String(row.title || 'Untitled Event'),
        date: dateKey,
        time: String(row.time || '09:00'),
        type,
        description: row.description || '',
        link_url: row.link_url || '',
        color: row.color || fallbackColor,
        google_event_id: row.google_event_id || '',
        google_event_url: row.google_event_url || '',
        google_meet_url: row.google_meet_url || '',
        assigned_user_ids: Array.isArray(row.assigned_user_ids) ? row.assigned_user_ids : [],
        assigned_users: Array.isArray(row.assigned_users) ? row.assigned_users : [],
        synced_to_google: Boolean(row.synced_to_google || row.google_event_id),
        statusKey: 'planned',
      };
    })
    .filter(Boolean);
};

const buildTaskEvents = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((task) => {
      const dueDate = toDateKey(task?.due_date);
      if (!dueDate) return null;
      const statusKey = normalizeStatusKey(task?.status);
      const taskColor = statusKey === 'completed' ? '#2e7d32' : '#ed6c02';
      return {
        id: `task-${task.id}`,
        source: 'task',
        sourceId: task.id,
        sourceLabel: EVENT_SOURCE_LABELS.task,
        title: task.title || 'Task',
        date: dueDate,
        time: '09:00',
        type: 'task',
        color: taskColor,
        statusKey,
        description: [
          task.project_name ? `Project: ${task.project_name}` : null,
          task.priority ? `Priority: ${task.priority}` : null,
          task.status ? `Status: ${task.status}` : null,
        ].filter(Boolean).join(' • '),
      };
    })
    .filter(Boolean);
};

const buildProjectEvents = (rows) => {
  if (!Array.isArray(rows)) return [];
  const output = [];

  rows.forEach((project) => {
    const statusKey = normalizeStatusKey(project?.status);
    const startDate = toDateKey(project?.start_date);
    const endDate = toDateKey(project?.end_date);
    const name = String(project?.name || 'Project');
    const client = String(project?.client_company_name || project?.company_name || '').trim();
    const baseDescription = [client ? `Client: ${client}` : null, project?.status ? `Status: ${project.status}` : null]
      .filter(Boolean)
      .join(' • ');

    if (startDate) {
      output.push({
        id: `project-${project.id}-start-${startDate}`,
        source: 'project',
        sourceId: project.id,
        sourceLabel: EVENT_SOURCE_LABELS.project,
        title: `Start: ${name}`,
        date: startDate,
        time: '09:00',
        type: 'project',
        color: '#2e7d32',
        statusKey,
        description: baseDescription,
      });
    }

    if (endDate) {
      const isClosed = statusKey === 'completed' || statusKey === 'cancelled';
      const isLate = dayjs(endDate).isBefore(dayjs(), 'day') && !isClosed;
      output.push({
        id: `project-${project.id}-end-${endDate}`,
        source: 'project',
        sourceId: project.id,
        sourceLabel: EVENT_SOURCE_LABELS.project,
        title: `Due: ${name}`,
        date: endDate,
        time: '17:00',
        type: 'deadline',
        color: isLate ? '#d32f2f' : '#1976d2',
        statusKey,
        description: baseDescription,
      });
    }
  });

  return output;
};

export default function CalendarPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [calendarApiAvailable, setCalendarApiAvailable] = useState(true);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState(INITIAL_GOOGLE_STATUS);
  const [loadingGoogleStatus, setLoadingGoogleStatus] = useState(true);
  const [googleActionLoading, setGoogleActionLoading] = useState(false);

  const [manualEvents, setManualEvents] = useState([]);
  const [taskEvents, setTaskEvents] = useState([]);
  const [projectEvents, setProjectEvents] = useState([]);
  const [users, setUsers] = useState([]);

  const [sourceFilter, setSourceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDayAgenda, setOpenDayAgenda] = useState(false);
  const [openNewEvent, setOpenNewEvent] = useState(false);

  const [newEvent, setNewEvent] = useState({
    title: '',
    date: dayjs().format('YYYY-MM-DD'),
    time: '09:00',
    type: 'meeting',
    description: '',
    link_url: '',
    assigned_user_ids: [],
    sync_to_google: false,
    create_google_meet: false,
  });

  const refreshGoogleCalendarStatus = async () => {
    setLoadingGoogleStatus(true);
    try {
      const data = await calendarService.getGoogleStatus();
      setGoogleCalendarStatus({
        configured: Boolean(data?.configured),
        connected: Boolean(data?.connected),
        google_email: data?.google_email || '',
        calendar_id: data?.calendar_id || 'primary',
        expiry_date: data?.expiry_date || null,
      });
    } catch (statusError) {
      setGoogleCalendarStatus(INITIAL_GOOGLE_STATUS);
    } finally {
      setLoadingGoogleStatus(false);
    }
  };

  useEffect(() => {
    const loadCalendarData = async () => {
      setLoading(true);
      setError('');
      setInfo('');

      try {
        await refreshGoogleCalendarStatus();

        let loadedManualEvents = [];
        try {
          const data = await calendarService.getEvents();
          if (data?.success) {
            loadedManualEvents = normalizeManualEvents(data.events);
            setCalendarApiAvailable(true);
          } else {
            throw new Error('Calendar API response invalid');
          }
        } catch (calendarError) {
          loadedManualEvents = normalizeManualEvents(loadLocalEvents());
          setCalendarApiAvailable(false);
          setInfo('Calendar API is not available. New events will be saved locally.');
        }
        setManualEvents(loadedManualEvents);

        try {
          const taskData = await taskService.getTasks();
          const tasks = Array.isArray(taskData) ? taskData : (taskData.tasks || []);
          setTaskEvents(buildTaskEvents(tasks));
        } catch (taskError) {
          setTaskEvents([]);
        }

        try {
          const projectData = await projectService.getProjects();
          const projects = Array.isArray(projectData) ? projectData : (projectData.projects || []);
          setProjectEvents(buildProjectEvents(projects));
        } catch (projectError) {
          setProjectEvents([]);
        }

        try {
          const usersData = await userService.getUsers();
          setUsers(Array.isArray(usersData?.users) ? usersData.users.filter((user) => user?.is_active !== 0) : []);
        } catch (usersError) {
          setUsers([]);
        }
      } catch (loadError) {
        setError('Error loading calendar data.');
      } finally {
        setLoading(false);
      }
    };

    loadCalendarData();
  }, []);

  useEffect(() => {
    const googleCalendarParam = searchParams.get('google_calendar');
    if (!googleCalendarParam) return;

    if (googleCalendarParam === 'connected') {
      setInfo('Google Calendar connected successfully.');
      refreshGoogleCalendarStatus();
    } else if (googleCalendarParam === 'error') {
      const rawMessage = searchParams.get('message') || 'unknown_error';
      setError(`Google Calendar connection failed: ${rawMessage.replace(/_/g, ' ')}`);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('google_calendar');
    nextParams.delete('message');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const allEvents = useMemo(() => {
    const merged = [...manualEvents, ...taskEvents, ...projectEvents];
    return merged.sort((a, b) => {
      const aDate = `${a.date}T${a.time || '00:00'}`;
      const bDate = `${b.date}T${b.time || '00:00'}`;
      return dayjs(aDate).valueOf() - dayjs(bDate).valueOf();
    });
  }, [manualEvents, taskEvents, projectEvents]);

  const filteredEvents = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    return allEvents.filter((event) => {
      if (sourceFilter !== 'all' && event.source !== sourceFilter) return false;
      if (typeFilter !== 'all' && event.type !== typeFilter) return false;
      if (!searchText) return true;

      const haystack = `${event.title} ${event.description || ''} ${event.sourceLabel || ''}`.toLowerCase();
      return haystack.includes(searchText);
    });
  }, [allEvents, sourceFilter, typeFilter, search]);

  const eventsByDate = useMemo(() => {
    const map = {};
    filteredEvents.forEach((event) => {
      if (!map[event.date]) map[event.date] = [];
      map[event.date].push(event);
    });
    return map;
  }, [filteredEvents]);

  const daysInMonth = useMemo(() => {
    const firstDay = currentMonth.startOf('month');
    const lastDay = currentMonth.endOf('month');
    const startDate = firstDay.startOf('week');
    const endDate = lastDay.endOf('week');
    const days = [];
    let day = startDate;

    while (day.isBefore(endDate) || day.isSame(endDate, 'day')) {
      days.push(day);
      day = day.add(1, 'day');
    }
    return days;
  }, [currentMonth]);

  const upcomingEvents = useMemo(() => {
    const today = dayjs().startOf('day');
    return filteredEvents
      .filter((event) => dayjs(event.date).isSame(today, 'day') || dayjs(event.date).isAfter(today))
      .slice(0, 8);
  }, [filteredEvents]);

  const selectedDateKey = selectedDate.format('YYYY-MM-DD');
  const selectedDateEvents = eventsByDate[selectedDateKey] || [];

  const stats = useMemo(() => {
    const today = dayjs().startOf('day');
    const overdue = filteredEvents.filter((event) => {
      const eventDay = dayjs(event.date);
      return eventDay.isBefore(today, 'day') && event.statusKey !== 'completed' && event.statusKey !== 'cancelled';
    }).length;

    const dueSoon = filteredEvents.filter((event) => {
      const eventDay = dayjs(event.date);
      const daysLeft = eventDay.diff(today, 'day');
      return daysLeft >= 0 && daysLeft <= 7 && event.statusKey !== 'completed' && event.statusKey !== 'cancelled';
    }).length;

    return {
      total: filteredEvents.length,
      manual: filteredEvents.filter((event) => event.source === 'manual').length,
      tasks: filteredEvents.filter((event) => event.source === 'task').length,
      projects: filteredEvents.filter((event) => event.source === 'project').length,
      overdue,
      dueSoon,
    };
  }, [filteredEvents]);

  const selectedAssignedUsers = useMemo(
    () => users.filter((user) => newEvent.assigned_user_ids.includes(user.id)),
    [users, newEvent.assigned_user_ids]
  );

  const handlePreviousMonth = () => setCurrentMonth((prev) => prev.subtract(1, 'month'));
  const handleNextMonth = () => setCurrentMonth((prev) => prev.add(1, 'month'));
  const handleToday = () => setCurrentMonth(dayjs());

  const openEventDetails = (event) => {
    setSelectedEvent(event);
    setOpenDialog(true);
  };

  const handleDayClick = (day) => {
    const dateStr = day.format('YYYY-MM-DD');
    const dayEvents = eventsByDate[dateStr] || [];
    setSelectedDate(day);

    if (!dayEvents.length) {
      setNewEvent((prev) => ({ ...prev, date: dateStr }));
      setOpenNewEvent(true);
      return;
    }

    setOpenDayAgenda(true);
  };

  const resetNewEventForm = () => {
    setNewEvent({
      title: '',
      date: dayjs().format('YYYY-MM-DD'),
      time: '09:00',
      type: 'meeting',
      description: '',
      link_url: '',
      assigned_user_ids: [],
      sync_to_google: false,
      create_google_meet: false,
    });
  };

  const handleConnectGoogleCalendar = async () => {
    try {
      setGoogleActionLoading(true);
      const data = await calendarService.getGoogleAuthUrl();
      if (!data?.success || !data?.url) throw new Error('Invalid Google Calendar auth response');
      window.location.assign(data.url);
    } catch (connectError) {
      setError(`Error starting Google Calendar connection: ${connectError.response?.data?.message || connectError.message}`);
      setGoogleActionLoading(false);
    }
  };

  const handleDisconnectGoogleCalendar = async () => {
    if (!window.confirm('Disconnect Google Calendar from your account?')) return;

    try {
      setGoogleActionLoading(true);
      await calendarService.disconnectGoogle();
      setGoogleCalendarStatus((prev) => ({
        ...prev,
        connected: false,
        google_email: '',
        calendar_id: 'primary',
        expiry_date: null,
      }));
      setInfo('Google Calendar disconnected.');
      setNewEvent((prev) => ({
        ...prev,
        sync_to_google: false,
        create_google_meet: false,
      }));
    } catch (disconnectError) {
      setError(`Error disconnecting Google Calendar: ${disconnectError.response?.data?.message || disconnectError.message}`);
    } finally {
      setGoogleActionLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    try {
      const eventType = EVENT_TYPES[newEvent.type] || EVENT_TYPES.meeting;
      const payload = {
        title: String(newEvent.title || '').trim(),
        date: newEvent.date,
        time: newEvent.time || '09:00',
        type: newEvent.type,
        description: String(newEvent.description || '').trim(),
        link_url: String(newEvent.link_url || '').trim(),
        assigned_user_ids: Array.isArray(newEvent.assigned_user_ids) ? newEvent.assigned_user_ids : [],
        color: eventType.color,
        sync_to_google: Boolean(newEvent.sync_to_google && googleCalendarStatus.connected),
        create_google_meet: Boolean(newEvent.create_google_meet && newEvent.sync_to_google && googleCalendarStatus.connected),
      };

      if (!payload.title) return;

      if (calendarApiAvailable) {
        try {
          const data = await calendarService.createEvent(payload);
          if (!data?.success || !data?.event) {
            throw new Error('Calendar API returned invalid create response');
          }
          setManualEvents((prev) => [...prev, ...normalizeManualEvents([data.event])]);
          if (data?.sync_warning) {
            setInfo(data.sync_warning);
          } else if (payload.sync_to_google) {
            setInfo(payload.create_google_meet ? 'Event synced to Google Calendar with Google Meet.' : 'Event synced to Google Calendar.');
          }
        } catch (createError) {
          const localEvent = normalizeManualEvents([{
            ...payload,
            id: `local-${Date.now()}`,
            assigned_users: users.filter((user) => payload.assigned_user_ids.includes(user.id)).map((user) => ({
              id: user.id,
              name: user.name || user.username || user.email,
              email: user.email || ''
            }))
          }]);
          const nextEvents = [...manualEvents, ...localEvent];
          setManualEvents(nextEvents);
          saveLocalEvents(nextEvents);
          setCalendarApiAvailable(false);
          setInfo('Calendar API is not available. Event was saved locally.');
        }
      } else {
        const localEvent = normalizeManualEvents([{
          ...payload,
          id: `local-${Date.now()}`,
          assigned_users: users.filter((user) => payload.assigned_user_ids.includes(user.id)).map((user) => ({
            id: user.id,
            name: user.name || user.username || user.email,
            email: user.email || ''
          }))
        }]);
        const nextEvents = [...manualEvents, ...localEvent];
        setManualEvents(nextEvents);
        saveLocalEvents(nextEvents);
      }

      setOpenNewEvent(false);
      resetNewEventForm();
    } catch (errorCreate) {
      setError(`Error creating event: ${errorCreate.message}`);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent || selectedEvent.source !== 'manual') return;
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    const eventId = selectedEvent.id;
    const isLocalId = String(eventId).startsWith('local-');

    try {
      if (!isLocalId && calendarApiAvailable) {
        const response = await calendarService.deleteEvent(eventId);
        if (response?.sync_warning) {
          setInfo(response.sync_warning);
        }
      }
      const next = manualEvents.filter((event) => event.id !== eventId);
      setManualEvents(next);
      if (!calendarApiAvailable || next.some((event) => String(event.id).startsWith('local-'))) {
        saveLocalEvents(next);
      } else {
        localStorage.removeItem(LOCAL_EVENTS_KEY);
      }
      setOpenDialog(false);
    } catch (deleteError) {
      setError(`Error deleting event: ${deleteError.response?.data?.message || deleteError.message}`);
    }
  };

  const handleOpenRelated = () => {
    if (!selectedEvent) return;
    if (selectedEvent.source === 'task') {
      navigate('/tasks');
      setOpenDialog(false);
      return;
    }
    if (selectedEvent.source === 'project' && selectedEvent.sourceId) {
      navigate(`/projects/${selectedEvent.sourceId}`);
      setOpenDialog(false);
    }
  };

  const CalendarDay = ({ day }) => {
    const dateStr = day.format('YYYY-MM-DD');
    const dayEvents = eventsByDate[dateStr] || [];
    const isToday = day.isSame(dayjs(), 'day');
    const isCurrentMonth = day.month() === currentMonth.month();

    return (
      <Box
        onClick={() => handleDayClick(day)}
        sx={{
          minHeight: 104,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: isToday ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.09), boxShadow: 1 },
          p: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: isToday ? 700 : 400,
              color: isCurrentMonth ? 'text.primary' : 'text.disabled',
            }}
          >
            {day.format('D')}
          </Typography>
          {isToday && <Chip label="Today" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {dayEvents.slice(0, 3).map((event) => (
            <Box
              key={event.id}
              sx={{
                bgcolor: alpha(event.color || '#1976d2', 0.15),
                borderLeft: `3px solid ${event.color || '#1976d2'}`,
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.73rem',
                  fontWeight: 600,
                  color: event.color || '#1976d2',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {event.title}
              </Typography>
            </Box>
          ))}

          {dayEvents.length > 3 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', pl: 0.5 }}>
              +{dayEvents.length - 3} more
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.35, md: 1.6 },
          mb: 1.5,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 0.8, display: 'flex' }}>
              <CalendarMonthIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.3rem' } }}>
                Calendar
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {currentMonth.format('MMMM YYYY')} • {stats.total} visible event{stats.total !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenNewEvent(true)}
            size="small"
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
            }}
          >
            {isMobile ? 'New' : 'New Event'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {info && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo('')}>
          {info}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 1.3,
          mb: 1.5,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: googleCalendarStatus.connected ? alpha('#2e7d32', 0.26) : alpha(theme.palette.primary.main, 0.18),
          background: googleCalendarStatus.connected
            ? `linear-gradient(135deg, ${alpha('#2e7d32', 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: googleCalendarStatus.connected ? alpha('#2e7d32', 0.16) : alpha(theme.palette.primary.main, 0.16),
                color: googleCalendarStatus.connected ? '#2e7d32' : 'primary.main'
              }}
            >
              {googleCalendarStatus.connected ? <CloudDoneIcon /> : <CloudOffIcon />}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Google Calendar
              </Typography>
              {loadingGoogleStatus ? (
                <Typography variant="body2" color="text.secondary">
                  Checking connection status...
                </Typography>
              ) : !googleCalendarStatus.configured ? (
                <Typography variant="body2" color="text.secondary">
                  The server is missing Google Calendar configuration.
                </Typography>
              ) : googleCalendarStatus.connected ? (
                <Typography variant="body2" color="text.secondary">
                  Connected as {googleCalendarStatus.google_email || 'Google user'}.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Connect your Google account to send manual events to Google Calendar and generate Meet links.
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {googleCalendarStatus.connected && (
              <Chip
                label={googleCalendarStatus.google_email || 'Connected'}
                size="small"
                color="success"
                variant="outlined"
              />
            )}
            {googleCalendarStatus.connected ? (
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisconnectGoogleCalendar}
                disabled={googleActionLoading}
              >
                Disconnect
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleConnectGoogleCalendar}
                disabled={googleActionLoading || loadingGoogleStatus || !googleCalendarStatus.configured}
              >
                Connect Google Calendar
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: 1.3, mb: 1.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FilterListIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Event, project or status..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              select
              size="small"
              label="Source"
              value={sourceFilter}
              onChange={(event) => setSourceFilter(event.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="task">Tasks</MenuItem>
              <MenuItem value="project">Projects</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField
              fullWidth
              select
              size="small"
              label="Type"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {Object.entries(EVENT_TYPES).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  {config.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Chip label={`Manual: ${stats.manual}`} size="small" />
          <Chip label={`Tasks: ${stats.tasks}`} size="small" />
          <Chip label={`Projects: ${stats.projects}`} size="small" />
          <Chip label={`Due Soon: ${stats.dueSoon}`} size="small" variant="outlined" sx={{ color: '#ed6c02', borderColor: alpha('#ed6c02', 0.4) }} />
          <Chip label={`Overdue: ${stats.overdue}`} size="small" variant="outlined" sx={{ color: '#d32f2f', borderColor: alpha('#d32f2f', 0.4) }} />
        </Stack>
      </Paper>

      {isMobile ? (
        <>
          <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <IconButton size="small" onClick={handlePreviousMonth}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', textTransform: 'capitalize' }}>
                {currentMonth.format('MMMM YYYY')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton size="small" onClick={handleToday}>
                  <TodayIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleNextMonth}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: 1, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <Box key={index} sx={{ textAlign: 'center', py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.7rem' }}>
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
              {daysInMonth.map((day) => {
                const dateStr = day.format('YYYY-MM-DD');
                const dayEvents = eventsByDate[dateStr] || [];
                const isToday = day.isSame(dayjs(), 'day');
                const isCurrentMonth = day.month() === currentMonth.month();
                const hasEvents = dayEvents.length > 0;

                return (
                  <Box
                    key={dateStr}
                    onClick={() => handleDayClick(day)}
                    sx={{
                      minHeight: 40,
                      border: '1px solid',
                      borderColor: isToday ? 'primary.main' : 'divider',
                      bgcolor: isToday ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                      p: 0.5,
                      borderRadius: 1,
                      position: 'relative',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: isToday ? 700 : 400,
                        color: isCurrentMonth ? 'text.primary' : 'text.disabled',
                        fontSize: '0.7rem',
                        display: 'block',
                        textAlign: 'center',
                      }}
                    >
                      {day.format('D')}
                    </Typography>
                    {hasEvents && (
                      <Box sx={{ position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 0.25 }}>
                        {dayEvents.slice(0, 3).map((event, idx) => (
                          <Box
                            key={`${event.id}-${idx}`}
                            sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: event.color || '#1976d2' }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: '1rem' }}>
              Upcoming Events
            </Typography>
            {upcomingEvents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No upcoming events
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {upcomingEvents.map((event) => {
                  const EventIconComponent = EVENT_TYPES[event.type]?.icon || EventIcon;
                  return (
                    <Card
                      key={event.id}
                      elevation={0}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': { boxShadow: 2 },
                      }}
                      onClick={() => openEventDetails(event)}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <Avatar sx={{ bgcolor: alpha(event.color || '#1976d2', 0.15), width: 32, height: 32 }}>
                            <EventIconComponent sx={{ color: event.color || '#1976d2', fontSize: 18 }} />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.25 }}>
                              {event.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {dayjs(event.date).format('MMM DD')} • {event.time || '09:00'}
                            </Typography>
                            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={EVENT_TYPES[event.type]?.label || 'Event'}
                                size="small"
                                sx={{ bgcolor: alpha(event.color || '#1976d2', 0.1), color: event.color || '#1976d2', fontSize: '0.65rem', height: 18 }}
                              />
                              <Chip label={event.sourceLabel || 'Source'} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={9}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                  {currentMonth.format('MMMM YYYY')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton size="small" onClick={handlePreviousMonth}>
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton size="small" onClick={handleToday}>
                    <TodayIcon />
                  </IconButton>
                  <IconButton size="small" onClick={handleNextMonth}>
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, mb: 1 }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <Box key={day} sx={{ textAlign: 'center', py: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      {day}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0 }}>
                {daysInMonth.map((day) => (
                  <CalendarDay key={day.format('YYYY-MM-DD')} day={day} />
                ))}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={3}>
            <Stack spacing={2}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Upcoming Events
                </Typography>
                {upcomingEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No upcoming events
                  </Typography>
                ) : (
                  <Stack spacing={1.25}>
                    {upcomingEvents.map((event) => {
                      const EventIconComponent = EVENT_TYPES[event.type]?.icon || EventIcon;
                      return (
                        <Card
                          key={event.id}
                          elevation={0}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': { boxShadow: 2, transform: 'translateY(-2px)' },
                          }}
                          onClick={() => openEventDetails(event)}
                        >
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Avatar sx={{ bgcolor: alpha(event.color || '#1976d2', 0.15), width: 32, height: 32 }}>
                                <EventIconComponent sx={{ color: event.color || '#1976d2', fontSize: 18 }} />
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {event.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {dayjs(event.date).format('MMM DD')} • {event.time || '09:00'}
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip label={event.sourceLabel || 'Source'} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                                </Box>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Paper>

              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Selected Day
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                  {selectedDate.format('MMMM DD, YYYY')}
                </Typography>
                {selectedDateEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No events for this day.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {selectedDateEvents.slice(0, 4).map((event) => (
                      <Box
                        key={`selected-${event.id}`}
                        onClick={() => openEventDetails(event)}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: alpha(event.color || '#1976d2', 0.3),
                          bgcolor: alpha(event.color || '#1976d2', 0.06),
                          cursor: 'pointer',
                        }}
                      >
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: event.color || '#1976d2' }}>
                          {event.time || '09:00'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {event.title}
                        </Typography>
                      </Box>
                    ))}
                    {selectedDateEvents.length > 4 && (
                      <Button size="small" onClick={() => setOpenDayAgenda(true)}>
                        View all ({selectedDateEvents.length})
                      </Button>
                    )}
                  </Stack>
                )}
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      )}

      <Dialog open={openDayAgenda} onClose={() => setOpenDayAgenda(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Agenda for {selectedDate.format('MMMM DD, YYYY')}
        </DialogTitle>
        <DialogContent dividers>
          {selectedDateEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No events for this day.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {selectedDateEvents.map((event) => {
                const EventIconComponent = EVENT_TYPES[event.type]?.icon || EventIcon;
                return (
                  <Card key={`day-${event.id}`} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', gap: 1.25 }}>
                        <Avatar sx={{ bgcolor: alpha(event.color || '#1976d2', 0.15), width: 32, height: 32 }}>
                          <EventIconComponent sx={{ color: event.color || '#1976d2', fontSize: 18 }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {event.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {event.time || '09:00'} • {event.sourceLabel || 'Source'}
                          </Typography>
                          {event.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.78rem' }}>
                              {event.description}
                            </Typography>
                          )}
                        </Box>
                        <Button size="small" onClick={() => { setOpenDayAgenda(false); openEventDetails(event); }}>
                          Open
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDayAgenda(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        {selectedEvent && (
          <>
            <DialogTitle sx={{ bgcolor: alpha(selectedEvent.color || '#1976d2', 0.1) }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: selectedEvent.color || '#1976d2' }}>
                  {React.createElement(EVENT_TYPES[selectedEvent.type]?.icon || EventIcon, { sx: { color: 'white' } })}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selectedEvent.title}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Chip
                      label={EVENT_TYPES[selectedEvent.type]?.label || 'Event'}
                      size="small"
                      sx={{ bgcolor: alpha(selectedEvent.color || '#1976d2', 0.2), color: selectedEvent.color || '#1976d2' }}
                    />
                    <Chip label={selectedEvent.sourceLabel || 'Source'} size="small" />
                    {selectedEvent.synced_to_google && (
                      <Chip label="Synced to Google" size="small" color="success" variant="outlined" />
                    )}
                  </Stack>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Date and Time
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(selectedEvent.date).format('MMMM DD, YYYY')} • {selectedEvent.time || '09:00'}
                  </Typography>
                </Box>
                {selectedEvent.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Description
                    </Typography>
                    <Typography variant="body2">{selectedEvent.description}</Typography>
                  </Box>
                )}
                {selectedEvent.link_url && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Link
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {selectedEvent.link_url}
                    </Typography>
                  </Box>
                )}
                {Array.isArray(selectedEvent.assigned_users) && selectedEvent.assigned_users.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                      Assigned Users
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {selectedEvent.assigned_users.map((user) => (
                        <Chip key={`assigned-${selectedEvent.id}-${user.id}`} label={user.name || user.email || `User ${user.id}`} size="small" />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>Close</Button>
              {(selectedEvent.source === 'task' || selectedEvent.source === 'project') && (
                <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={handleOpenRelated}>
                  Open {selectedEvent.source === 'task' ? 'Tasks' : 'Project'}
                </Button>
              )}
              {selectedEvent.source === 'manual' && selectedEvent.google_event_url && (
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  component="a"
                  href={selectedEvent.google_event_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Google Event
                </Button>
              )}
              {selectedEvent.source === 'manual' && selectedEvent.link_url && (
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  component="a"
                  href={selectedEvent.link_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Link
                </Button>
              )}
              {selectedEvent.source === 'manual' && selectedEvent.google_meet_url && (
                <Button
                  variant="contained"
                  startIcon={<VideoCallIcon />}
                  component="a"
                  href={selectedEvent.google_meet_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Join Meet
                </Button>
              )}
              {selectedEvent.source === 'manual' && (
                <Button color="error" startIcon={<DeleteIcon />} onClick={handleDeleteEvent}>
                  Delete
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={openNewEvent} onClose={() => setOpenNewEvent(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>New Event</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={newEvent.title}
                onChange={(event) => setNewEvent((prev) => ({ ...prev, title: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={newEvent.date}
                onChange={(event) => setNewEvent((prev) => ({ ...prev, date: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Time"
                value={newEvent.time}
                onChange={(event) => setNewEvent((prev) => ({ ...prev, time: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Type"
                value={newEvent.type}
                onChange={(event) => setNewEvent((prev) => ({ ...prev, type: event.target.value }))}
              >
                {Object.entries(EVENT_TYPES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={newEvent.description}
                onChange={(event) => setNewEvent((prev) => ({ ...prev, description: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link (optional)"
                placeholder="https://meet.google.com/... or client portal"
                value={newEvent.link_url}
                onChange={(event) => setNewEvent((prev) => ({ ...prev, link_url: event.target.value }))}
                helperText="Useful for meetings, portals, quotations, or any direct reference."
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={users}
                value={selectedAssignedUsers}
                onChange={(_, value) => {
                  setNewEvent((prev) => ({
                    ...prev,
                    assigned_user_ids: value.map((user) => user.id)
                  }));
                }}
                getOptionLabel={(option) => option.name || option.username || option.email || `User ${option.id}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign users"
                    helperText="Assigned users will receive a notification."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: newEvent.sync_to_google ? alpha('#2e7d32', 0.32) : 'divider',
                  bgcolor: newEvent.sync_to_google ? alpha('#2e7d32', 0.04) : 'background.paper'
                }}
              >
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(newEvent.sync_to_google && googleCalendarStatus.connected)}
                      onChange={(event) => setNewEvent((prev) => ({
                        ...prev,
                        sync_to_google: event.target.checked,
                        create_google_meet: event.target.checked ? prev.create_google_meet : false
                      }))}
                      disabled={!googleCalendarStatus.connected}
                    />
                  )}
                  label="Sync this event to Google Calendar"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {googleCalendarStatus.connected
                    ? `Connected as ${googleCalendarStatus.google_email || 'Google user'}`
                    : 'Connect Google Calendar first if you want this event to appear in your Google Calendar.'}
                </Typography>

                {newEvent.sync_to_google && googleCalendarStatus.connected && newEvent.type === 'meeting' && (
                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={Boolean(newEvent.create_google_meet)}
                          onChange={(event) => setNewEvent((prev) => ({
                            ...prev,
                            create_google_meet: event.target.checked
                          }))}
                        />
                      )}
                      label="Create Google Meet link"
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenNewEvent(false);
              resetNewEventForm();
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateEvent} disabled={!String(newEvent.title || '').trim()}>
            Create Event
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
